import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { formatTime12h, formatDate } from '../utils/timeFormat';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { ScrollArea } from './ui/scroll-area';
import { 
  Loader2, Calendar, Clock, MapPin, Users, Check, 
  AlertTriangle, Zap, ChevronRight, Star
} from 'lucide-react';
import { ReliabilityBadge } from './ReliabilityBadge';
import { toast } from 'sonner';
import GgfBadge from './GgfBadge';
import { useAuth } from '../context/AuthContext';

/**
 * QuickBookModal - Direct booking flow from Smart Suggestions
 * 
 * Allows clients to:
 * 1. Select a talent from recommendations
 * 2. See their open shifts needing staffing
 * 3. Assign talent to a shift with one click
 */

const ShiftOption = ({ shift, onSelect, isSelecting }) => {
  const positionsRemaining = shift.talent_needed - (shift.booked_count || shift.bookings_count || 0);
  const isUrgent = positionsRemaining > 0 && new Date(shift.shift_date) < new Date(Date.now() + 3 * 24 * 60 * 60 * 1000);
  
  return (
    <div 
      className={`p-3 rounded-lg border transition-all cursor-pointer ${
        isUrgent 
          ? 'border-red-200 bg-red-50/50 hover:border-red-300' 
          : 'border-gray-200 bg-white hover:border-cyan-300 hover:bg-cyan-50/30'
      }`}
      onClick={() => !isSelecting && onSelect(shift)}
      data-testid={`shift-option-${shift.id || shift.gig_id}`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-medium text-gray-900 text-sm truncate">
              {shift.venue_name || shift.event_name}
            </h4>
            {isUrgent && (
              <Badge variant="outline" className="text-[10px] bg-red-100 text-red-700 border-red-200">
                <AlertTriangle className="w-3 h-3 mr-1" />
                Urgent
              </Badge>
            )}
          </div>
          
          <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
            <span className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {formatDate(shift.shift_date)}
            </span>
            {shift.start_time && (
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {formatTime12h(shift.start_time)}
              </span>
            )}
            {shift.city && (
              <span className="flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {shift.city}
              </span>
            )}
          </div>
          
          <div className="flex items-center gap-2 mt-1.5">
            <Users className="w-3 h-3 text-gray-400" />
            <span className={`text-xs ${positionsRemaining > 0 ? 'text-amber-600' : 'text-green-600'}`}>
              {positionsRemaining > 0 
                ? `${positionsRemaining} position${positionsRemaining > 1 ? 's' : ''} remaining`
                : 'Fully staffed'
              }
            </span>
          </div>
        </div>
        
        <Button
          size="sm"
          className="shrink-0 bg-cyan-500 hover:bg-cyan-600 h-8"
          disabled={positionsRemaining <= 0 || isSelecting}
        >
          {isSelecting ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              <Zap className="w-3 h-3 mr-1" />
              Book
            </>
          )}
        </Button>
      </div>
      
      {shift.role_name && (
        <Badge variant="outline" className="mt-2 text-[10px] bg-gray-50">
          {shift.role_name}
        </Badge>
      )}
    </div>
  );
};

const QuickBookModal = ({ 
  isOpen, 
  onClose, 
  talent,
  onBookingComplete 
}) => {
  const { user } = useAuth();
  const isAdmin = ['admin', 'super_admin'].includes((user?.role || '').toLowerCase());
  const isGgfClient = /\b(guardian garage floors|ggf)\b/i.test(`${user?.company_name || ''} ${user?.name || ''}`);
  const [loading, setLoading] = useState(true);
  const [shifts, setShifts] = useState([]);
  const [booking, setBooking] = useState(false);
  const [bookingShiftId, setBookingShiftId] = useState(null);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    if (isOpen && talent) {
      fetchOpenShifts();
    }
  }, [isOpen, talent]);
  
  const fetchOpenShifts = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch client's open shifts that need staffing
      const res = await api.get('/client/shifts?status=open&limit=20');
      const allShifts = res.data.shifts || res.data || [];
      
      // Filter to shifts that have available spots
      const availableShifts = allShifts.filter(s => {
        const booked = s.booked_count || s.bookings_count || 0;
        const needed = s.talent_needed || 1;
        return booked < needed;
      }).sort((a, b) => new Date(a.shift_date) - new Date(b.shift_date));
      
      setShifts(availableShifts);
    } catch (err) {
      console.error('Failed to fetch shifts:', err);
      setError('Unable to load shifts');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSelectShift = async (shift) => {
    const shiftId = shift.id || shift.gig_id;
    setBooking(true);
    setBookingShiftId(shiftId);
    
    try {
      // Iter-241 fix: was POST /client/shifts/{id}/book which doesn't exist (404).
      // Canonical client booking endpoint is POST /client/bookings with body
      // { shift_id, talent_id }. See BookingCreate model in /app/backend/models.py.
      await api.post(`/client/bookings`, {
        shift_id: shiftId,
        talent_id: talent.talent_id,
      });
      
      toast.success(
        `${talent.display_name} has been booked for ${shift.venue_name || shift.event_name}!`,
        {
          action: {
            label: 'View Shift',
            onClick: () => window.location.href = `/client/shifts/${shiftId}`
          }
        }
      );
      
      onBookingComplete?.(shiftId, talent.talent_id);
      onClose();
      
    } catch (err) {
      console.error('Booking failed:', err);
      const errorMsg = err.response?.data?.detail || 'Failed to create booking';
      toast.error(errorMsg);
    } finally {
      setBooking(false);
      setBookingShiftId(null);
    }
  };
  
  if (!talent) return null;
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-amber-500" />
            Quick Book
          </DialogTitle>
          <DialogDescription>
            Assign {talent.display_name} to one of your open shifts
          </DialogDescription>
        </DialogHeader>
        
        {/* Talent Preview */}
        <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
          <div className="flex items-center gap-3">
            <Avatar className="w-12 h-12">
              <AvatarImage src={talent.profile_photo_url} alt={talent.display_name} />
              <AvatarFallback className="bg-amber-100 text-amber-700">
                {talent.first_name?.[0]}{talent.last_name?.[0]}
              </AvatarFallback>
            </Avatar>
            <div>
              <h4 className="font-semibold text-gray-900">{talent.display_name}</h4>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                {talent.reliability_badge && talent.reliability_badge !== 'new_talent' && (
                  <ReliabilityBadge badge={talent.reliability_badge} size="xs" showScore={false} />
                )}
                {talent.global_rating > 0 && (
                  <span className="flex items-center gap-1 text-xs text-amber-600">
                    <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                    {talent.global_rating.toFixed(1)}
                  </span>
                )}
                {talent.booking_count > 0 && (
                  <span className="text-xs text-gray-500">
                    {talent.booking_count} previous booking{talent.booking_count > 1 ? 's' : ''}
                  </span>
                )}
                {talent.ggf_certified && (
                  <GgfBadge
                    certified
                    score={talent.ggf_test_score}
                    date={talent.ggf_certified_at}
                    size="xs"
                    showScore
                    context={{ isAdmin, isGgfClient }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* Shifts List */}
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
            Select a shift
            {shifts.length > 0 && (
              <Badge variant="outline" className="text-[10px]">
                {shifts.length} available
              </Badge>
            )}
          </h4>
          
          {loading ? (
            <div className="py-8 flex justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-cyan-500" />
            </div>
          ) : error ? (
            <div className="py-8 text-center">
              <p className="text-sm text-red-600">{error}</p>
              <Button variant="outline" size="sm" className="mt-2" onClick={fetchOpenShifts}>
                Try Again
              </Button>
            </div>
          ) : shifts.length === 0 ? (
            <div className="py-8 text-center bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">No open shifts available</p>
              <p className="text-xs text-gray-500 mt-1">
                Post a new shift to book this talent
              </p>
              <Button 
                variant="outline" 
                size="sm" 
                className="mt-3"
                onClick={() => {
                  onClose();
                  window.location.href = '/client/shifts/create';
                }}
              >
                Post a Shift
              </Button>
            </div>
          ) : (
            <ScrollArea className="h-[280px] pr-2">
              <div className="space-y-2">
                {shifts.map(shift => (
                  <ShiftOption
                    key={shift.id || shift.gig_id}
                    shift={shift}
                    onSelect={handleSelectShift}
                    isSelecting={booking && bookingShiftId === (shift.id || shift.gig_id)}
                  />
                ))}
              </div>
            </ScrollArea>
          )}
        </div>
        
        {/* Footer */}
        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default QuickBookModal;
